<!-- Patient_ContactEntries.vue -->
<template>
  <div>
    <div class="flex items-center gap-2 mb-2">
      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contact</span>
      <button @click="showAdd = !showAdd" class="text-xs text-blue-600 hover:text-blue-700 font-medium">+ Add</button>
      <div v-if="hasDNC" class="ml-auto flex items-center gap-1">
        <FontAwesomeIcon :icon="['fas', 'ban']" class="text-red-500 text-xs" />
        <span class="text-xs font-medium text-red-600">DNC</span>
      </div>
    </div>

    <!-- Add row -->
    <div v-if="showAdd" class="mb-2 p-2 bg-gray-50 rounded flex gap-2 items-end flex-wrap">
      <select v-model="addForm.contact_type" @change="addForm.value = ''" class="px-1.5 py-1 border border-gray-300 rounded text-xs">
        <option value="phone">Phone</option>
        <option value="email">Email</option>
        <option value="address">Address</option>
      </select>
      <input
        v-model="addForm.value"
        v-maska="addForm.contact_type === 'phone' ? { mask: '(###) ###-####' } : {}"
        :type="addForm.contact_type === 'phone' ? 'tel' : 'text'"
        class="flex-1 min-w-[120px] px-2 py-1 border border-gray-300 rounded text-xs"
        :placeholder="addForm.contact_type === 'phone' ? '(555) 123-4567' : 'Value'"
      />
      <select v-model="addForm.label" class="px-1.5 py-1 border border-gray-300 rounded text-xs">
        <option value="unknown">Label</option>
        <option value="mobile">Mobile</option>
        <option value="home">Home</option>
        <option value="work">Work</option>
        <option value="primary">Primary</option>
      </select>
      <button @click="addEntry" :disabled="!addFormValid" class="btn-primary-sm text-xs">Add</button>
      <button @click="showAdd = false" class="text-xs text-gray-500">Cancel</button>
    </div>

    <div v-if="loading" class="space-y-1.5">
      <div v-for="n in 4" :key="'sk-' + n" class="flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0">
        <div class="w-3 h-3 bg-gray-200 rounded animate-pulse mt-0.5 shrink-0"></div>
        <div class="flex-1 space-y-1">
          <div class="h-3 bg-gray-200 rounded animate-pulse" :style="{ width: n % 2 === 0 ? '60%' : '45%' }"></div>
          <div class="h-2.5 w-12 bg-gray-100 rounded animate-pulse"></div>
        </div>
      </div>
    </div>

    <div v-else-if="allEntries.length > 0" class="space-y-1.5">
      <div
        v-for="(entry, idx) in allEntries"
        :key="idx"
        :class="[
          'flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0',
          entry.status === 'do_not_contact' ? 'opacity-50' : ''
        ]"
      >
        <!-- Type icon -->
        <FontAwesomeIcon
          :icon="['fas', entry.contact_type === 'phone' ? 'phone' : entry.contact_type === 'email' ? 'message' : 'map-pin']"
          class="text-xs text-gray-400 mt-0.5 w-3 shrink-0"
        />

        <!-- Value + meta -->
        <div class="flex-1 min-w-0">
          <!-- Inline edit (user-created rows only) -->
          <div v-if="editingId && editingId === entry.id" class="flex items-center gap-1.5 flex-wrap">
            <input
              v-model="editForm.contact_value"
              v-maska="entry.contact_type === 'phone' ? { mask: '(###) ###-####' } : {}"
              :type="entry.contact_type === 'phone' ? 'tel' : 'text'"
              class="sensitive flex-1 min-w-[120px] px-2 py-1 border border-gray-300 rounded text-xs"
              @keyup.enter="saveEdit(entry)"
              @keyup.esc="cancelEdit"
            />
            <select v-model="editForm.label" class="px-1.5 py-1 border border-gray-300 rounded text-xs">
              <option value="unknown">Unknown</option>
              <option value="mobile">Mobile</option>
              <option value="home">Home</option>
              <option value="work">Work</option>
              <option value="fax">Fax</option>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
            </select>
            <button
              class="btn-primary-sm text-xs"
              :disabled="!editFormValid(entry) || savingEdit"
              @click="saveEdit(entry)"
            >{{ savingEdit ? 'Saving…' : 'Save' }}</button>
            <button class="text-xs text-gray-500" @click="cancelEdit">Cancel</button>
          </div>
          <div v-else class="flex items-center gap-2">
            <span class="sensitive text-xs text-gray-900 font-medium">{{ displayValue(entry) }}</span>
            <span v-if="entry.label && entry.label !== 'unknown'" class="text-xs text-gray-400 capitalize">{{ entry.label }}</span>
            <span v-if="entry.source" class="text-xs text-gray-400 capitalize">· {{ entry.source }}</span>
          </div>
          <div v-if="entry.status !== 'active'" class="text-xs text-gray-400">
            {{ { do_not_contact: 'Do Not Contact', disconnected: 'Disconnected', wrong_number: 'Wrong #', no_answer: 'No Answer', voicemail_only: 'VM Only' }[entry.status] || entry.status }}
          </div>
        </div>

        <!-- Actions -->
        <div v-if="!(editingId && editingId === entry.id)" class="flex items-center gap-1 shrink-0">
          <!-- Edit is only offered on rows a USER created. A row sourced from
               ADT/EHR/payer mirrors upstream data — editing its value would be
               overwritten on the next sync and look like data loss, so the
               backend refuses it and we don't offer the affordance. -->
          <button v-if="canEdit(entry)" @click="startEdit(entry)"
            class="text-gray-300 hover:text-blue-600" title="Edit">
            <FontAwesomeIcon :icon="['fas', 'pen']" class="text-xs" />
          </button>
          <button @click="verify(entry)"
            :class="entry.status === 'active' && entry.last_checked_at ? 'text-green-500' : 'text-gray-300 hover:text-green-500'"
            title="Verify">
            <FontAwesomeIcon :icon="['fas', 'thumbs-up']" class="text-xs" />
          </button>
          <button @click="saveField(entry, 'status', entry.status === 'disconnected' ? 'active' : 'disconnected')"
            :class="entry.status === 'disconnected' ? 'text-red-500' : 'text-gray-300 hover:text-red-500'"
            title="Bad number">
            <FontAwesomeIcon :icon="['fas', 'thumbs-down']" class="text-xs" />
          </button>
          <button v-if="entry.status !== 'do_not_contact'" @click="saveField(entry, 'status', 'do_not_contact')"
            class="text-gray-300 hover:text-red-600" title="Do Not Contact">
            <FontAwesomeIcon :icon="['fas', 'ban']" class="text-xs" />
          </button>
          <button v-else @click="saveField(entry, 'status', 'active')"
            class="text-red-500 hover:text-green-600" title="Remove DNC">
            <FontAwesomeIcon :icon="['fas', 'sync']" class="text-xs" />
          </button>
        </div>
      </div>
    </div>

    <p v-else class="text-xs text-gray-400">No contact information on file</p>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { vMaska } from 'maska/vue';
import abortable from '@/mixins/abortable';

export default {
  name: 'Patient_ContactEntries',
  directives: { maska: vMaska },
  components: { FontAwesomeIcon },
  mixins: [abortable],
  props: {
    patientIdentifier: { type: String, required: true },
    payerName: { type: String, default: null },
  },
  emits: ['show-message'],
  data() {
    return {
      loading: false,
      entries: { phone: [], email: [], address: [] },
      showAdd: false,
      addForm: { contact_type: 'phone', value: '', label: 'unknown' },
      editingId: null,
      editForm: { contact_value: '', label: 'unknown' },
      savingEdit: false,
    };
  },
  computed: {
    allEntries() {
      return [...(this.entries.phone || []), ...(this.entries.email || []), ...(this.entries.address || [])];
    },
    hasDNC() {
      return this.allEntries.some(e => e.status === 'do_not_contact');
    },
    addFormValid() {
      if (!this.addForm.value) return false;
      if (this.addForm.contact_type === 'phone') {
        return this.addForm.value.replace(/\D/g, '').length === 10;
      }
      return true;
    },
  },
  watch: {
    patientIdentifier: {
      immediate: true,
      handler(val) {
        if (val) {
          const signal = this._renewAbortSignal();
          this.fetch(signal);
        }
      }
    }
  },
  methods: {
    _id() {
      if (this.patientIdentifier.length === 11 && /^[A-Za-z0-9]+$/.test(this.patientIdentifier))
        return { bene_mbi: this.patientIdentifier };
      const p = { member_id: this.patientIdentifier };
      if (this.payerName) p.payer_name = this.payerName;
      return p;
    },

    async fetch(signal) {
      this.loading = true;
      try {
        const params = new URLSearchParams(this._id());
        const res = await this.$axios.get(
          `/api/quality/patient-contact/entries/?${params.toString()}`,
          { signal },
        );
        if (res.data.success) this.entries = res.data.entries;
      } catch (e) {
        if (this._isAborted(e)) return;
        console.error('Error fetching contact entries:', e);
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },

    // Only user-created rows are editable, and only once they have a real PK
    // (source rows with no PatientContactEntry come back with id === null).
    canEdit(entry) {
      return !!entry.id && (entry.source || 'user') === 'user';
    },
    startEdit(entry) {
      this.editingId = entry.id;
      let val = entry.contact_value;
      if (entry.contact_type === 'phone') {
        const d = val.replace(/\D/g, '');
        val = d.length === 11 && d[0] === '1' ? d.slice(1) : d;
      }
      this.editForm = {
        contact_value: val,
        label: entry.label || 'unknown',
      };
    },
    cancelEdit() {
      this.editingId = null;
      this.editForm = { contact_value: '', label: 'unknown' };
    },
    editFormValid(entry) {
      if (!this.editForm.contact_value) return false;
      if (entry.contact_type === 'phone') {
        return this.editForm.contact_value.replace(/\D/g, '').length === 10;
      }
      return true;
    },

    async saveEdit(entry) {
      if (!this.editFormValid(entry)) return;
      this.savingEdit = true;
      try {
        const value = entry.contact_type === 'phone'
          ? this.editForm.contact_value.replace(/\D/g, '')
          : this.editForm.contact_value;
        const res = await this.$axios.post('/api/quality/patient-contact/entry/update/', {
          ...this._id(),
          entry_id: entry.id,
          contact_value: value,
          label: this.editForm.label,
        });
        if (res.data.entries) this.entries = res.data.entries;
        this.cancelEdit();
        this.$emit('show-message', 'Updated', 'success');
      } catch (e) {
        // The backend returns a specific reason (duplicate value, source-owned
        // row, empty value) — surface it rather than a generic failure.
        const msg = e.response?.data?.error || 'Failed to update';
        this.$emit('show-message', msg, 'error');
      } finally {
        this.savingEdit = false;
      }
    },
    async addEntry() {
      if (!this.addFormValid) return;
      try {
        const value = this.addForm.contact_type === 'phone'
          ? this.addForm.value.replace(/\D/g, '')
          : this.addForm.value;
        const res = await this.$axios.post('/api/quality/patient-contact/entry/add/', {
          ...this._id(), contact_type: this.addForm.contact_type, contact_value: value, label: this.addForm.label,
        });
        if (res.data.success) {
          this.entries = res.data.entries;
          this.addForm = { contact_type: 'phone', value: '', label: 'unknown' };
          this.showAdd = false;
          this.$emit('show-message', 'Added', 'success');
        }
      } catch (e) {
        console.error('Error adding:', e);
        this.$emit('show-message', 'Failed to add', 'error');
      }
    },

    async verify(entry) {
      await this.saveField(entry, 'status', 'active');
    },

    async saveField(entry, field, value) {
      try {
        const res = await this.$axios.post('/api/quality/patient-contact/entry/save/', {
          ...this._id(), contact_type: entry.contact_type, contact_value: entry.contact_value, [field]: value,
        });
        if (res.data.success) {
          this.entries = res.data.entries;
          this.$emit('show-message', 'Updated', 'success');
        }
      } catch (e) {
        console.error('Error saving:', e);
        this.$emit('show-message', 'Failed to update', 'error');
      }
    },

    displayValue(entry) {
      if (entry.contact_type === 'phone') {
        const n = String(entry.contact_value).replace(/\D/g, '');
        if (n.length === 10) return `(${n.slice(0,3)}) ${n.slice(3,6)}-${n.slice(6)}`;
        if (n.length === 11 && n[0] === '1') return `(${n.slice(1,4)}) ${n.slice(4,7)}-${n.slice(7)}`;
        return entry.contact_value;
      }
      if (entry.contact_type === 'address') {
        try {
          const a = JSON.parse(entry.contact_value);
          const parts = [a.street];
          if (a.street_2) parts.push(a.street_2);
          parts.push([a.city, a.state].filter(Boolean).join(', ') + (a.zip ? ' ' + a.zip : ''));
          return parts.filter(Boolean).join(', ');
        } catch { return entry.contact_value; }
      }
      return entry.contact_value;
    },

    fmtDate(s) {
      if (!s) return '';
      const p = String(s).substring(0, 10).split('-');
      if (p.length !== 3) return s;
      return new Date(p[0], p[1] - 1, p[2]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
  }
};
</script>
